import { useCmsNew } from "@/lib/cms/cms-new-i18n";
import {
  type BlockStyle,
  type ResponsiveFlowPatch,
  type StyleDevice,
} from "@/lib/cms-puck/block-style";
import type {
  LayoutAlign,
  LayoutDirection,
  LayoutJustify,
} from "@/lib/cms-puck/layout";
import { CMS_PALETTE_TEXT_INPUT_CLASS } from "@/components/cms/theme/cms-color-input-styles";
import {
  PuckSafeSelect as Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/cms-puck/fields/PuckSafeSelect";
import { cn } from "@/lib/utils";
import { StyleSection } from "./StyleSection";
import { ControlRow } from "./controls";
import {
  CATALOG_GRID_BLOCKS,
  inheritedFlow,
  maxFlowColumns,
  validFlowColumns,
} from "./flow-inheritance";

/** Valeur de Select signifiant « hérite » (Radix interdit la valeur vide). */
const INHERIT = "inherit";

/**
 * Disposition d'un conteneur en tablette / mobile (`style.responsive.*.flow`).
 * Les valeurs Bureau restent les props du bloc (onglet Contenu) et s'affichent
 * comme valeurs héritées. Champs selon le bloc : direction et répartition pour
 * le Conteneur (flex), colonnes et écarts horizontal / vertical pour Colonnes et
 * Grille. Vide = hérite. Libellés repris des champs Bureau équivalents.
 */
export function FlowControls({
  device,
  style,
  blockType,
  blockProps,
  onChange,
}: {
  device: StyleDevice;
  style: BlockStyle;
  blockType: string;
  /** Props du bloc : valeurs Bureau affichées comme héritées. */
  blockProps: Record<string, unknown>;
  onChange: (
    device: "tablet" | "mobile",
    patch: Partial<ResponsiveFlowPatch>,
  ) => void;
}) {
  const cmsNew = useCmsNew();
  const title = cmsNew(
    "cms.new.fields.style.flow.title",
    "Disposition par appareil",
  );

  if (device === "desktop") {
    return (
      <StyleSection title={title}>
        <p className="text-xs leading-relaxed text-slate-500">
          {cmsNew(
            "cms.new.fields.style.flow.desktopHint",
            "Les valeurs Bureau se règlent dans l'onglet Contenu. Choisissez Tablette ou Mobile pour les adapter.",
          )}
        </p>
      </StyleSection>
    );
  }

  const flow = style.responsive[device].flow;
  const inherited = inheritedFlow(device, blockType, blockProps, style);
  const isContainer = blockType === "Container";
  const isCatalogGrid = CATALOG_GRID_BLOCKS.has(blockType);
  const set = (patch: Partial<ResponsiveFlowPatch>) => onChange(device, patch);
  const inheritLabel = cmsNew("cms.new.fields.style.flow.inherit", "Hérite");

  const directions: Array<[LayoutDirection, string]> = [
    ["row", cmsNew("cms.new.auto.label.ligne", "Ligne")],
    ["column", cmsNew("cms.new.auto.label.colonne", "Colonne")],
  ];
  const aligns: Array<[LayoutAlign, string]> = [
    ["start", cmsNew("cms.new.fields.layoutAlign.start", "Haut")],
    ["center", cmsNew("cms.new.fields.layoutAlign.center", "Centre")],
    ["end", cmsNew("cms.new.fields.layoutAlign.end", "Bas")],
    ["stretch", cmsNew("cms.new.fields.layoutAlign.stretch", "Étiré")],
  ];
  const justifies: Array<[LayoutJustify, string]> = [
    ["start", cmsNew("cms.new.fields.layoutJustify.start", "Début")],
    ["center", cmsNew("cms.new.fields.layoutJustify.center", "Centre")],
    ["end", cmsNew("cms.new.fields.layoutJustify.end", "Fin")],
    ["between", cmsNew("cms.new.fields.layoutJustify.between", "Espacé")],
  ];

  const choiceRow = <T extends string>(
    label: string,
    value: T | null,
    inheritedValue: T | null,
    options: Array<[T, string]>,
    onValue: (value: T | null) => void,
  ) => (
    <ControlRow label={label}>
      <Select
        value={value ?? INHERIT}
        onValueChange={(v) => onValue(v === INHERIT ? null : (v as T))}
      >
        <SelectTrigger aria-label={label} className="h-9 w-44 bg-white">
          <SelectValue />
        </SelectTrigger>
        <SelectContent>
          <SelectItem value={INHERIT}>
            {inheritedValue
              ? `${inheritLabel} (${options.find(([v]) => v === inheritedValue)?.[1] ?? inheritedValue})`
              : inheritLabel}
          </SelectItem>
          {options.map(([v, l]) => (
            <SelectItem key={v} value={v}>
              {l}
            </SelectItem>
          ))}
        </SelectContent>
      </Select>
    </ControlRow>
  );

  // Vide = hérite ; toute autre saisie est ramenée dans les bornes (entier),
  // pour ne jamais vider le champ sous les doigts de l'utilisateur. Sans
  // `max`, c'est un écart en px (unité affichée).
  const numberRow = (
    label: string,
    value: number | null,
    inheritedValue: number | null,
    onValue: (value: number | null) => void,
    bounds: { min: number; max?: number } = { min: 0 },
  ) => (
    <ControlRow label={label}>
      <input
        aria-label={label}
        type="number"
        min={bounds.min}
        max={bounds.max}
        step={1}
        value={value ?? ""}
        placeholder={
          inheritedValue != null ? String(inheritedValue) : inheritLabel
        }
        className={cn(
          CMS_PALETTE_TEXT_INPUT_CLASS,
          "h-9 w-24 text-center tabular-nums",
        )}
        onChange={(e) => {
          const raw = e.target.value;
          if (raw === "") return onValue(null);
          const n = Number(raw);
          if (!Number.isFinite(n)) return;
          const max = bounds.max ?? Number.POSITIVE_INFINITY;
          onValue(Math.min(max, Math.max(bounds.min, Math.round(n))));
        }}
      />
      {bounds.max == null ? (
        <span className="text-xs text-slate-500">px</span>
      ) : null}
    </ControlRow>
  );

  return (
    <StyleSection
      title={title}
      description={cmsNew(
        "cms.new.auto.description.surcharges_pour_cet_appareil_valeur_vide_heritee_du_niveau_superieur",
        "Surcharges pour cet appareil. Valeur vide = héritée du niveau supérieur.",
      )}
    >
      <div className="space-y-4">
        {isContainer
          ? choiceRow(
              cmsNew("cms.new.auto.label.direction", "Direction"),
              flow.direction,
              inherited.direction,
              directions,
              (direction) => set({ direction }),
            )
          : numberRow(
              cmsNew("cms.new.fields.columnCount.colonnes", "Colonnes"),
              validFlowColumns(flow.columns, blockType, blockProps),
              inherited.columns,
              (columns) => set({ columns }),
              { min: 1, max: maxFlowColumns(blockType, blockProps) },
            )}
        {/* Grilles du catalogue : pas d'alignement en Bureau (onglet Contenu). */}
        {isCatalogGrid
          ? null
          : choiceRow(
              isContainer
                ? cmsNew(
                    "cms.new.auto.label.alignement_transversal",
                    "Alignement (transversal)",
                  )
                : cmsNew(
                    "cms.new.auto.label.alignement_vertical",
                    "Alignement vertical",
                  ),
              flow.alignItems,
              inherited.alignItems,
              aligns,
              (alignItems) => set({ alignItems }),
            )}
        {isContainer
          ? choiceRow(
              cmsNew(
                "cms.new.auto.label.justification_principal",
                "Justification (principal)",
              ),
              flow.justify,
              inherited.justify,
              justifies,
              (justify) => set({ justify }),
            )
          : null}
        {isContainer ? (
          numberRow(
            cmsNew("cms.new.auto.label.espacement", "Espacement"),
            flow.gap,
            inherited.gap,
            (gap) => set({ gap }),
          )
        ) : (
          <>
            {numberRow(
              cmsNew(
                "cms.new.auto.label.espacement_horizontal",
                "Espacement horizontal",
              ),
              flow.columnGap,
              inherited.columnGap,
              (columnGap) => set({ columnGap }),
            )}
            {numberRow(
              cmsNew(
                "cms.new.auto.label.espacement_vertical",
                "Espacement vertical",
              ),
              flow.rowGap,
              inherited.rowGap,
              (rowGap) => set({ rowGap }),
            )}
          </>
        )}
      </div>
    </StyleSection>
  );
}
